import React, { useState } from "react";
import { LuHash, LuPackageSearch, LuSearch } from "react-icons/lu";

import Button from "../ui/Button";
import Input from "../ui/Input";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import { EmptyState, LoadingState } from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldIconClass,
  fieldInputClass,
} from "../ui/styles";
import { inCodeRange } from "../../static/itemCode";

export type PickerFilter = { search: string; codeFrom: string; codeTo: string };

export const noFilter: PickerFilter = { search: "", codeFrom: "", codeTo: "" };

// Whether a card passes the filter. The search finds the item ID or code, or one of the card's other texts (a receive
// card's "#12"); the code range keeps codes from one code to another, read as the items list reads them (AB-9 before
// AB-10).
export const matchesFilter = (
  filter: PickerFilter,
  itemId: number,
  code: string,
  others: string[] = []
) => {
  const query = filter.search.toLowerCase();

  return (
    (!query ||
      String(itemId).includes(query) ||
      code.toLowerCase().includes(query) ||
      others.some((text) => text.toLowerCase().includes(query))) &&
    inCodeRange(code, filter.codeFrom, filter.codeTo)
  );
};

// The filter bar's buttons, as tall as its boxes.
const filterClearClass =
  "inline-flex h-12 items-center justify-center whitespace-nowrap rounded-lg border-2 border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50";
const filterApplyClass =
  "inline-flex h-12 items-center justify-center whitespace-nowrap rounded-lg bg-[#113F67] px-4 text-sm font-medium text-white hover:bg-[#0d2f4f]";

type Props = {
  // The category the cards belong to, after the title.
  categoryLabel?: string;
  // Called with the filter to use on "ફિલ્ટર લાગુ કરો" (or Enter) and on "ફિલ્ટર સાફ કરો"; typing alone filters nothing.
  onFilter: (filter: PickerFilter) => void;
  isLoading: boolean;
  // What to say instead of the cards, when none are shown.
  emptyLabel: string | null;
  pickedCount: number;
  onClose: () => void;
  // The cards.
  children: React.ReactNode;
};

// The item picker of the cutting order and receive forms: its title, the filter bar of the reference app, the cards and
// "પસંદ કરેલ આઈટમ: N". There is no separate save; closing it in any way hands the picker's lines to the form.
const CuttingPickerDialog = ({
  categoryLabel,
  onFilter,
  isLoading,
  emptyLabel,
  pickedCount,
  onClose,
  children,
}: Props) => {
  // What the filter boxes hold.
  const [draft, setDraft] = useState<PickerFilter>(noFilter);

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onFilter({
      search: draft.search.trim(),
      codeFrom: draft.codeFrom.trim(),
      codeTo: draft.codeTo.trim(),
    });
  };

  const handleClearFilter = () => {
    setDraft(noFilter);
    onFilter(noFilter);
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent
        size="wide"
        className={cx(
          "font-inter w-full grid-rows-[auto_auto_minmax(0,1fr)_auto] sm:h-[90vh] sm:max-w-[calc(100vw-4rem)]",
          dialogMobileContentClass
        )}
      >
        <DialogHeader
          className={cx(
            "border-b border-[#113F67] pb-4",
            dialogMobileHeaderClass
          )}
        >
          <div className="flex items-center gap-4 pr-6">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#113F67]/10">
              <LuPackageSearch className="h-6 w-6 text-[#113F67]" />
            </span>
            <h2 className={cx(dialogTitleClass, "mb-2 min-w-0")}>
              આઈટમ પસંદ કરો
              {categoryLabel && (
                <span className="font-normal text-gray-500">
                  {" "}
                  - {categoryLabel}
                </span>
              )}
            </h2>
          </div>
        </DialogHeader>

        <form
          className="grid grid-cols-2 gap-2 max-sm:mx-6 max-sm:mt-4 sm:flex sm:items-center"
          onSubmit={handleApplyFilter}
        >
          <div className="relative col-span-2 sm:min-w-0 sm:flex-1">
            <LuSearch className={fieldIconClass} />
            <Input
              type="text"
              value={draft.search}
              placeholder="ID, કોડ શોધો..."
              aria-label="ID, કોડ શોધો"
              className={fieldInputClass()}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
          </div>
          <div className="relative sm:w-44">
            <LuHash className={fieldIconClass} />
            <Input
              type="text"
              value={draft.codeFrom}
              placeholder="કોડ થી"
              aria-label="કોડ થી"
              className={fieldInputClass()}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  codeFrom: event.target.value,
                }))
              }
            />
          </div>
          <div className="relative sm:w-44">
            <LuHash className={fieldIconClass} />
            <Input
              type="text"
              value={draft.codeTo}
              placeholder="કોડ સુધી"
              aria-label="કોડ સુધી"
              className={fieldInputClass()}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  codeTo: event.target.value,
                }))
              }
            />
          </div>
          <button
            type="button"
            className={filterClearClass}
            onClick={handleClearFilter}
          >
            ફિલ્ટર સાફ કરો
          </button>
          <button type="submit" className={filterApplyClass}>
            ફિલ્ટર લાગુ કરો
          </button>
        </form>

        <div className={cx("min-h-0 overflow-y-auto", dialogMobileBodyClass)}>
          {isLoading ? (
            <LoadingState />
          ) : emptyLabel !== null ? (
            <EmptyState label={emptyLabel} />
          ) : (
            <div className="grid grid-cols-2 items-start gap-4 p-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {children}
            </div>
          )}
        </div>

        <DialogFooter
          className={cx("items-center justify-between", dialogMobileFooterClass)}
        >
          <span className="text-sm font-semibold text-[#113F67]">
            પસંદ કરેલ આઈટમ: {pickedCount}
          </span>

          <Button variant="outline" className="min-w-40" onClick={onClose}>
            બંધ કરો
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CuttingPickerDialog;
